/**
 * Ascent guidance and attitude commanding.
 *
 * Guidance is a **pure function of the current state**. Given a position,
 * velocity, mass and mission target it returns a thrust direction and a throttle,
 * with no internal memory. That is a deliberate constraint: it makes the whole
 * flight reproducible from its initial conditions, which is what lets the
 * simulator offer rewind, replay and recording without storing every frame, and
 * what lets the optimizer evaluate a candidate trajectory deterministically.
 *
 * ## The ascent profile
 *
 * A real launch is four distinct problems:
 *
 * 1. **Vertical rise** — clear the tower, gain a little altitude before pitching.
 * 2. **Gravity turn** — pitch over on a smooth programme, trading vertical speed
 *    for horizontal speed while dynamic pressure is significant.
 * 3. **Coast** — shut down once the *predicted apoapsis* reaches the target, then
 *    fall upward to it. Burning past this point wastes propellant lifting an
 *    apoapsis that is already high enough.
 * 4. **Circularisation** — a second burn at apoapsis raising periapsis into orbit.
 *
 * Step 3 is closed-loop on the instantaneous orbital elements rather than
 * open-loop on a timer, which is why the same programme works across a wide range
 * of vehicles without retuning.
 *
 * ## Attitude fidelity (Tier B)
 *
 * The commanded thrust direction is achieved instantaneously, subject to an
 * angle-of-attack limit while dynamic pressure is high. Full 6-DOF rigid-body
 * dynamics with gimbal actuator lag is not modelled; see
 * `docs/09-mathematical-models.md`.
 */

import { EARTH, type CelestialBodyParams } from './constants.js';
import {
  altitudeAbove,
  atmosphereRelativeVelocity,
  ecefToEci,
  enuToEcef,
  geodeticToEcef,
  type Geodetic,
} from './frames.js';
import { meanFromTrue, meanMotion, type OrbitalElements } from './kepler.js';
import { describeOrbit } from './orbits.js';
import { gmst } from './time.js';
import { clamp, wrapTwoPi } from './units.js';
import {
  angleBetween,
  cross,
  dot,
  norm,
  normalize,
  rotateAbout,
  scale,
  type ReadonlyVec3,
  type Vec3,
} from './vec3.js';

export type AttitudeMode =
  /** Along the local vertical. */
  | 'vertical'
  /** Along the pitch programme, in the launch plane. */
  | 'pitch-program'
  /** Along the velocity relative to the rotating atmosphere (zero angle of attack). */
  | 'prograde-relative'
  /** Along the inertial velocity. */
  | 'prograde'
  | 'retrograde'
  | 'normal'
  | 'anti-normal'
  | 'radial-out'
  | 'radial-in'
  /** Engines off. */
  | 'coast';

export type FlightPhase =
  | 'pre-launch'
  | 'vertical-rise'
  | 'pitch-over'
  | 'gravity-turn'
  | 'coast-to-apoapsis'
  | 'circularisation'
  | 'orbit'
  /** Coast in the launch body's parking orbit before a departure burn. */
  | 'parking-orbit'
  /** Hyperbolic departure from the launch body's sphere of influence. */
  | 'departure'
  /** Earth-centred coast from trans-lunar injection to the Moon. */
  | 'trans-lunar'
  /** Sun-centred coast between planetary spheres of influence. */
  | 'interplanetary-coast'
  /** Encounter and capture operations at the destination body. */
  | 'arrival'
  /** Bound coast around the destination body after capture. */
  | 'destination-orbit'
  | 'maneuver'
  | 'reentry'
  | 'landed';

export interface AscentProgram {
  /** Launch azimuth measured clockwise from true north, rad. */
  azimuth: number;
  /** Altitude at which the pitch-over begins, m. */
  pitchStartAltitude: number;
  /** Altitude at which the programme reaches fully horizontal, m. */
  turnEndAltitude: number;
  /**
   * Shape exponent of the pitch programme. 0.5 gives the classic gravity turn;
   * lower pitches over more aggressively early, higher stays vertical longer.
   */
  turnExponent: number;
  /** Target apoapsis altitude, m. */
  targetApoapsisAltitude: number;
  /** Target periapsis altitude, m. */
  targetPeriapsisAltitude: number;
  /**
   * Altitude above which guidance switches from the open-loop pitch programme to
   * closed-loop orbital insertion, m. Below this the vehicle is in air thick
   * enough that attitude must follow a pre-planned, low-angle-of-attack profile;
   * above it, steering can be driven by the orbit it is trying to reach.
   */
  closedLoopAltitude: number;
  /**
   * Time constant for levelling off the climb during closed-loop insertion, s.
   * Larger values hold the climb longer and loft the trajectory higher.
   */
  verticalSpeedTimeConstant: number;
  /** Maximum angle of attack permitted while dynamic pressure is high, rad. */
  maxAngleOfAttack: number;
  /** Dynamic pressure above which the angle-of-attack limit applies, Pa. */
  angleOfAttackQThreshold: number;
  /** Dynamic pressure at which to throttle down through max-Q, Pa. Zero disables. */
  maxQThrottleThreshold: number;
  /** Throttle setting used while above the max-Q threshold. */
  maxQThrottle: number;
}

/** A sensible default ascent programme for a low circular orbit. */
export function defaultAscentProgram(
  targetAltitude = 200_000,
  azimuth = Math.PI / 2,
): AscentProgram {
  return {
    azimuth,
    pitchStartAltitude: 500,
    // Reaching fully horizontal only well above the atmosphere is what lofts the
    // trajectory. A programme that flattens out by 70 km leaves a low-thrust
    // upper stage with no vertical thrust component at staging, and it falls back
    // into the atmosphere before it can build orbital speed.
    turnEndAltitude: 120_000,
    turnExponent: 0.5,
    targetApoapsisAltitude: targetAltitude,
    targetPeriapsisAltitude: targetAltitude,
    closedLoopAltitude: 55_000,
    verticalSpeedTimeConstant: 200,
    maxAngleOfAttack: (6 * Math.PI) / 180,
    angleOfAttackQThreshold: 2000,
    maxQThrottleThreshold: 0,
    maxQThrottle: 0.7,
  };
}

/**
 * Inertial launch azimuth needed to reach a target inclination from a given
 * latitude, rad.
 *
 * From the spherical relation `cos(i) = cos(latitude) * sin(azimuth)`. Returns
 * `null` when the inclination is unreachable directly — you cannot launch into an
 * inclination lower than your latitude without a dog-leg or a plane change, which
 * is exactly why Baikonur cannot reach a 28-degree orbit and Cape Canaveral can.
 *
 * This is the *inertial* azimuth. For the azimuth to actually steer by, use
 * `launchAzimuthCorrectedForRotation` — the difference is several degrees of
 * inclination, which matters a great deal for a sun-synchronous orbit.
 */
export function launchAzimuthForInclination(
  latitude: number,
  inclination: number,
): number | null {
  const cosLat = Math.cos(latitude);
  if (Math.abs(cosLat) < 1e-12) return null;
  const sinAz = Math.cos(inclination) / cosLat;
  if (sinAz < -1 || sinAz > 1) return null;
  return Math.asin(sinAz);
}

/**
 * Ground-relative launch azimuth needed to reach a target inclination, rad,
 * corrected for the planet's rotation.
 *
 * The spherical formula gives the azimuth of the *inertial* velocity at orbit
 * insertion. But the vehicle starts already moving east at up to 465 m/s, so the
 * heading it must actually fly differs. Decomposing the required inertial
 * velocity into east and north components and subtracting the site's own eastward
 * motion gives the ground track to steer:
 *
 *     v_east  = v_orbit * sin(azimuth_inertial) - omega * R * cos(latitude)
 *     v_north = v_orbit * cos(azimuth_inertial)
 *     azimuth_ground = atan2(v_east, v_north)
 *
 * Neglecting this is a real error, not a nicety: aiming a Vandenberg launch at the
 * uncorrected azimuth for a 98.2 degree sun-synchronous orbit actually delivers
 * about 95.4 degrees, and a sun-synchronous orbit three degrees off its design
 * inclination does not stay sun-synchronous.
 *
 * @param descending Choose the southerly solution. Both azimuths reach the same
 *   inclination; range-safety corridors decide which one is available.
 */
export function launchAzimuthCorrectedForRotation(
  latitude: number,
  inclination: number,
  orbitalSpeed: number,
  descending = inclination > Math.PI / 2,
  body: CelestialBodyParams = EARTH,
): number | null {
  const inertial = launchAzimuthForInclination(latitude, inclination);
  if (inertial === null) return null;

  const inertialAzimuth = descending ? Math.PI - inertial : inertial;
  const siteSpeed = body.rotationRate * body.equatorialRadius * Math.cos(latitude);

  const east = orbitalSpeed * Math.sin(inertialAzimuth) - siteSpeed;
  const north = orbitalSpeed * Math.cos(inertialAzimuth);
  return wrapTwoPi(Math.atan2(east, north));
}

/**
 * Inclination reached by launching due east from a latitude, rad — the cheapest
 * available inclination from that site, equal to the latitude itself.
 */
export const minimumInclinationFromSite = (latitude: number): number => Math.abs(latitude);

/**
 * Velocity gained from the planet's rotation for a given launch azimuth, m/s.
 * Maximum due east at the equator, zero at the poles, negative flying west.
 */
export function rotationalVelocityBonus(
  latitude: number,
  azimuth: number,
  body: CelestialBodyParams = EARTH,
): number {
  const siteSpeed = body.rotationRate * body.equatorialRadius * Math.cos(latitude);
  return siteSpeed * Math.sin(azimuth);
}

/** Commanded pitch angle from the local vertical at a given altitude, rad. */
export function programmedPitch(altitude: number, program: AscentProgram): number {
  if (altitude <= program.pitchStartAltitude) return 0;
  if (altitude >= program.turnEndAltitude) return Math.PI / 2;
  const span = program.turnEndAltitude - program.pitchStartAltitude;
  const fraction = (altitude - program.pitchStartAltitude) / span;
  return (Math.PI / 2) * clamp(fraction, 0, 1) ** program.turnExponent;
}

/**
 * Unit vector pointing downrange at the launch site, in ECI.
 *
 * Combines the site's East–North–Up basis with the launch azimuth. This vector
 * plus the site position define the launch plane, and therefore the orbit's
 * inclination.
 */
export function launchDirectionEci(site: Geodetic, azimuth: number, epochSeconds: number): Vec3 {
  // Azimuth is clockwise from north, so it decomposes as (east, north) =
  // (sin, cos) — the reverse of the usual mathematical convention.
  const enu: Vec3 = [Math.sin(azimuth), Math.cos(azimuth), 0];
  return normalize(ecefToEci(enuToEcef(enu, site), gmst(epochSeconds)));
}

/**
 * Normal to the launch plane, in ECI. Held fixed for the whole ascent so the
 * vehicle flies a planar trajectory and arrives at the intended inclination.
 *
 * The plane is spanned by the site's *position vector* and the downrange
 * direction, because an orbit is a conic about the body's centre and must contain
 * that centre. Using the local vertical instead would tilt the plane by the
 * geodetic-versus-geocentric latitude difference — up to 0.19 degrees, which is
 * a visible inclination error.
 */
export function launchPlaneNormal(
  site: Geodetic,
  azimuth: number,
  epochSeconds: number,
  body: CelestialBodyParams = EARTH,
): Vec3 {
  const sitePosition = ecefToEci(geodeticToEcef(site, body), gmst(epochSeconds));
  const downrange = launchDirectionEci(site, azimuth, epochSeconds);
  return normalize(cross(sitePosition, downrange));
}

export interface GuidanceContext {
  /** Fixed normal to the launch plane, ECI. */
  planeNormal: ReadonlyVec3;
  program: AscentProgram;
  body: CelestialBodyParams;
  /** Dynamic pressure at the current state, Pa. */
  dynamicPressure: number;
  /**
   * Thrust acceleration available at full throttle, m/s^2. The closed-loop
   * insertion law needs it: how far the vehicle can afford to pitch over depends
   * entirely on how much thrust it has left to hold itself up with.
   */
  availableThrustAcceleration: number;
  /** True once the ascent stage has been discarded and only the upper stage remains. */
  finalStage?: boolean;
}

/**
 * Closed-loop insertion pitch: the angle from the local vertical that balances the
 * vehicle's climb while it builds horizontal speed.
 *
 * Above the atmosphere the vertical equation of motion is
 *
 *     a_vertical = a_thrust * cos(theta) - (g - v_horizontal^2 / r)
 *
 * where the bracket is the *net* downward acceleration: gravity less the
 * centrifugal relief the vehicle has already earned. Solving for the pitch that
 * delivers a chosen vertical acceleration gives
 *
 *     cos(theta) = (netDown + a_vertical_desired) / a_thrust
 *
 * The desired vertical acceleration is simply "bleed the climb rate to zero over
 * `verticalSpeedTimeConstant` seconds", which produces exactly the behaviour a
 * real ascent has: pitch over gradually, arrive at the target altitude with the
 * climb arrested and the speed horizontal.
 *
 * The law is self-correcting in two ways that make it work across wildly
 * different vehicles without retuning. As propellant burns off, `a_thrust` rises,
 * so `cos(theta)` falls and the vehicle pitches further over. As horizontal speed
 * builds, the centrifugal term grows, `netDown` shrinks, and the vehicle pitches
 * over faster still — until at orbital speed `netDown` reaches zero and the
 * commanded attitude is fully horizontal.
 */
export function closedLoopInsertionPitch(
  position: ReadonlyVec3,
  velocity: ReadonlyVec3,
  program: AscentProgram,
  availableThrustAcceleration: number,
  body: CelestialBodyParams = EARTH,
): number {
  const r = norm(position);
  if (r === 0) return 0;
  const radial = normalize(position);

  const verticalSpeed = dot(velocity, radial);
  const speedSquared = dot(velocity, velocity);
  const horizontalSpeedSquared = Math.max(0, speedSquared - verticalSpeed * verticalSpeed);

  const gravity = body.mu / (r * r);
  const centrifugal = horizontalSpeedSquared / r;
  const netDown = gravity - centrifugal;

  // Bleed the climb rate toward zero. Once the target apoapsis is in hand there
  // is no reason to keep climbing at all, so the target is simply zero.
  const desiredVerticalAcceleration = -verticalSpeed / program.verticalSpeedTimeConstant;

  const thrust = Math.max(availableThrustAcceleration, 1e-6);
  const cosTheta = clamp((netDown + desiredVerticalAcceleration) / thrust, -1, 1);
  return Math.acos(cosTheta);
}

/**
 * Time until the next apoapsis passage, s. Infinite for an open orbit.
 *
 * Apoapsis sits at mean anomaly pi, so the answer follows directly from the mean
 * anomaly and the mean motion. This is smooth and monotonically decreasing during
 * a coast, which is exactly what a switching condition needs — unlike a radius
 * comparison, which flattens out near a circular orbit.
 */
export function timeToApoapsis(elements: OrbitalElements, mu: number): number {
  if (elements.eccentricity >= 1 || !Number.isFinite(elements.semiMajorAxis)) {
    return Number.POSITIVE_INFINITY;
  }
  const meanAnomaly = meanFromTrue(elements.trueAnomaly, elements.eccentricity);
  const n = meanMotion(elements.semiMajorAxis, mu);
  if (n === 0) return Number.POSITIVE_INFINITY;
  const delta = wrapTwoPi(Math.PI - meanAnomaly);
  return delta / n;
}

/**
 * How long a circularisation burn at apoapsis will take, s.
 *
 * Used to size the burn window so the impulse straddles apoapsis rather than
 * starting there, which is what a real vehicle does and what keeps the resulting
 * orbit round.
 */
export function circularisationBurnDuration(
  apoapsisRadius: number,
  semiMajorAxis: number,
  thrustAcceleration: number,
  body: CelestialBodyParams = EARTH,
): number {
  if (thrustAcceleration <= 0) return Number.POSITIVE_INFINITY;
  const speedAtApoapsis = Math.sqrt(
    Math.max(0, body.mu * (2 / apoapsisRadius - 1 / semiMajorAxis)),
  );
  const circular = Math.sqrt(body.mu / apoapsisRadius);
  return Math.abs(circular - speedAtApoapsis) / thrustAcceleration;
}

/**
 * How much more speed the vehicle needs, right now, to be on the target orbit, m/s.
 * Negative once it already has enough.
 *
 * This is the quantity terminal guidance actually acts on. It is reported back to
 * the propagator so that the integration step can be shortened as the burn nears
 * completion: insertion accuracy is set by how much velocity accrues between
 * cutoff decisions, so precision comes from **cutoff timing**, not from throttling
 * down.
 *
 * Throttling was the obvious alternative and it does not work. Most real engines
 * cannot throttle below 40-70 percent of rated thrust, so a taper that asks for
 * ten percent gets either full thrust or a shutdown — and the resulting orbit is
 * wrong by tens of kilometres either way. Cutting off at the right instant needs no
 * throttle authority at all.
 */
export function speedShortfallToOrbit(
  position: ReadonlyVec3,
  velocity: ReadonlyVec3,
  targetApoapsisRadius: number,
  targetPeriapsisRadius: number,
  body: CelestialBodyParams = EARTH,
): number {
  const r = norm(position);
  if (r === 0) return Number.POSITIVE_INFINITY;

  const targetSemiMajorAxis = (targetApoapsisRadius + targetPeriapsisRadius) / 2;
  const requiredSpeedSquared = body.mu * (2 / r - 1 / targetSemiMajorAxis);
  // A negative value means the target orbit does not reach this radius at all, so
  // there is nothing left to gain here.
  if (requiredSpeedSquared <= 0) return Number.NEGATIVE_INFINITY;

  return Math.sqrt(requiredSpeedSquared) - norm(velocity);
}

export interface GuidanceCommand {
  /** Unit thrust direction in ECI. */
  direction: Vec3;
  /** Throttle setting, 0-1. */
  throttle: number;
  phase: FlightPhase;
  /** Commanded pitch from the local vertical, rad. Reported for the telemetry display. */
  commandedPitch: number;
  /** Achieved angle of attack after the aerodynamic limit is applied, rad. */
  angleOfAttack: number;
  /**
   * Speed still required to be on the target orbit, m/s. The propagator uses this
   * to shorten its step as an insertion burn nears completion, so that cutoff
   * lands precisely. Negative or absent when no burn is being targeted.
   */
  remainingDeltaV?: number;
}

/**
 * Rotate `commanded` toward `reference` so the angle between them does not exceed
 * `limit`. Used to keep the angle of attack within structural limits while the
 * vehicle is in dense air, where flying at a large angle to the airflow would
 * break a real vehicle apart.
 */
export function limitAngleTo(
  commanded: ReadonlyVec3,
  reference: ReadonlyVec3,
  limit: number,
): Vec3 {
  const angle = angleBetween(commanded, reference);
  if (angle <= limit || angle === 0) return normalize(commanded);
  const axis = cross(reference, commanded);
  if (norm(axis) < 1e-12) return normalize(commanded);
  return normalize(rotateAbout(normalize(reference), axis, limit));
}

/**
 * Compute the guidance command for the current state.
 *
 * Phase selection is closed-loop on the osculating orbit, not on a schedule.
 */
export function ascentGuidance(
  position: ReadonlyVec3,
  velocity: ReadonlyVec3,
  context: GuidanceContext,
): GuidanceCommand {
  const { program, body, planeNormal } = context;
  const radial = normalize(position);
  const altitude = altitudeAbove(position, body);

  // Downrange direction in the launch plane at the current position.
  const downrangeRaw = cross(planeNormal, radial);
  const downrange =
    norm(downrangeRaw) < 1e-9 ? normalize(velocity) : normalize(downrangeRaw);

  const orbit = describeOrbit(position, velocity, body);
  const targetApoapsisRadius = body.equatorialRadius + program.targetApoapsisAltitude;
  const targetPeriapsisRadius = body.equatorialRadius + program.targetPeriapsisAltitude;

  const relativeVelocity = atmosphereRelativeVelocity(position, velocity, body);
  const relativeSpeed = norm(relativeVelocity);
  const climbing = orbit.flightPathAngle > 0;

  // --- Orbit reached: nothing left to do.
  //
  // The tolerance is a few kilometres rather than metres. Insertion accuracy of
  // that order is what real vehicles deliver, and demanding more leaves the
  // guidance chasing the J2 oscillation of the osculating elements forever.
  const insertionTolerance = 3000;
  const onTargetOrbit =
    orbit.periapsisRadius >= targetPeriapsisRadius - insertionTolerance &&
    orbit.apoapsisRadius >= targetApoapsisRadius - insertionTolerance;

  // A vehicle with a large upper-stage margin can overshoot the requested
  // apoapsis badly on this generic programme — Saturn V carries 12.8 km/s of ideal
  // delta-V for a job needing 9.4. Once apoapsis has run well past the target,
  // continuing to thrust prograde raises it further instead of raising periapsis,
  // so the burn must stop as soon as the orbit is *stable*, even though it is not
  // the orbit that was asked for. The alternative is thrusting until the
  // propellant is gone. Tailoring the ascent to such a vehicle is the Trajectory
  // Optimizer's job, not this law's.
  const stableButOvershot =
    orbit.periapsisAltitude >= 140_000 &&
    orbit.apoapsisRadius >= targetApoapsisRadius * 1.25;

  if ((onTargetOrbit || stableButOvershot) && !orbit.impacts) {
    return {
      direction: normalize(velocity),
      throttle: 0,
      phase: 'orbit',
      commandedPitch: Math.PI / 2,
      angleOfAttack: 0,
    };
  }

  // --- Apoapsis high enough but periapsis too low: coast, then circularise.
  if (orbit.apoapsisRadius >= targetApoapsisRadius && orbit.periapsisRadius < targetPeriapsisRadius) {
    // Whether to start the burn is decided on *time* to apoapsis, not on a radius
    // ratio. For a nearly circular orbit r/r_apoapsis is always close to 1 — it
    // measures eccentricity, not proximity to apoapsis — so a ratio threshold
    // latches on far too early and never releases.
    const burnDuration = circularisationBurnDuration(
      orbit.apoapsisRadius,
      orbit.elements.semiMajorAxis,
      context.availableThrustAcceleration,
      body,
    );
    // The window has a floor so that a long coast step cannot step straight over
    // it. Starting marginally early costs a little apoapsis and is what real
    // vehicles do anyway.
    const halfWindow = Math.max(burnDuration / 2, 20);
    const shortfall = speedShortfallToOrbit(
      position,
      velocity,
      Math.max(orbit.apoapsisRadius, targetApoapsisRadius),
      targetPeriapsisRadius,
      body,
    );
    const toApoapsis = timeToApoapsis(orbit.elements, body.mu);
    const sinceApoapsis = Number.isFinite(orbit.period)
      ? orbit.period - toApoapsis
      : Number.POSITIVE_INFINITY;

    // Burn while inside a window straddling apoapsis, so the impulse is centred
    // on it. Two other cases also demand thrust:
    //
    // - Nearly finished: with only a few tens of m/s left, completing the burn now
    //   beats coasting a whole revolution for a better burn point. The test is on
    //   the *shortfall*, which falls monotonically while thrusting, so once this
    //   latches it stays latched until the orbit is made. An equivalent test on
    //   eccentricity does not have that property — eccentricity rises and falls
    //   during the burn, so the engine chatters on and off across the threshold,
    //   wasting propellant a few hundred kilograms at a time.
    // - Descending: the last chance to save a trajectory that would fall back.
    const nearlyFinished = shortfall > 0 && shortfall < 50;
    if (Math.min(toApoapsis, sinceApoapsis) <= halfWindow || nearlyFinished || !climbing) {
      return {
        direction: normalize(velocity),
        throttle: 1,
        phase: 'circularisation',
        commandedPitch: Math.PI / 2,
        angleOfAttack: 0,
        remainingDeltaV: shortfall,
      };
    }
    return {
      direction: normalize(velocity),
      throttle: 0,
      phase: 'coast-to-apoapsis',
      commandedPitch: Math.PI / 2,
      angleOfAttack: 0,
      remainingDeltaV: shortfall,
    };
  }

  // --- Powered ascent.
  //
  // Two regimes. In dense air the attitude must follow the pre-planned pitch
  // programme so the angle of attack stays small; above that, steering is driven
  // closed-loop by the orbit being targeted.
  const useClosedLoop = altitude >= program.closedLoopAltitude;
  const pitch = useClosedLoop
    ? closedLoopInsertionPitch(position, velocity, program, context.availableThrustAcceleration, body)
    : programmedPitch(altitude, program);

  let phase: FlightPhase;
  if (altitude <= program.pitchStartAltitude) phase = 'vertical-rise';
  else if (altitude < program.turnEndAltitude * 0.25) phase = 'pitch-over';
  else phase = 'gravity-turn';

  let direction: Vec3 = normalize([
    radial[0] * Math.cos(pitch) + downrange[0] * Math.sin(pitch),
    radial[1] * Math.cos(pitch) + downrange[1] * Math.sin(pitch),
    radial[2] * Math.cos(pitch) + downrange[2] * Math.sin(pitch),
  ]);

  // Aerodynamic angle-of-attack limit, applied only where the air can hurt.
  let angleOfAttack = 0;
  if (
    context.dynamicPressure > program.angleOfAttackQThreshold &&
    relativeSpeed > 30
  ) {
    const limited = limitAngleTo(direction, relativeVelocity, program.maxAngleOfAttack);
    angleOfAttack = angleBetween(limited, relativeVelocity);
    direction = limited;
  } else if (relativeSpeed > 1) {
    angleOfAttack = angleBetween(direction, relativeVelocity);
  }

  // Throttle down through max-Q if configured.
  let throttle = 1;
  if (
    program.maxQThrottleThreshold > 0 &&
    context.dynamicPressure > program.maxQThrottleThreshold
  ) {
    throttle = program.maxQThrottle;
  }

  // No terminal taper during ascent. Cutoff is governed by the apoapsis test
  // above, and the resulting overshoot is bounded by the velocity one guidance
  // interval adds — a few metres per second, or single-digit kilometres of
  // apoapsis. Tapering here needs a target conic, and while periapsis is still
  // far underground no sensible one exists: the naive construction yields an
  // imaginary required speed and shuts the engine down mid-ascent.

  return { direction, throttle, phase, commandedPitch: pitch, angleOfAttack };
}

/**
 * Direction for a named attitude mode. Used by the manoeuvre executor and by the
 * reentry and landing guidance.
 */
export function attitudeDirection(
  mode: AttitudeMode,
  position: ReadonlyVec3,
  velocity: ReadonlyVec3,
  body: CelestialBodyParams = EARTH,
): Vec3 {
  switch (mode) {
    case 'vertical':
      return normalize(position);
    case 'prograde':
      return normalize(velocity);
    case 'retrograde':
      return scale(normalize(velocity), -1);
    case 'prograde-relative':
      return normalize(atmosphereRelativeVelocity(position, velocity, body));
    case 'normal':
      return normalize(cross(position, velocity));
    case 'anti-normal':
      return scale(normalize(cross(position, velocity)), -1);
    case 'radial-out':
      return normalize(position);
    case 'radial-in':
      return scale(normalize(position), -1);
    case 'pitch-program':
    case 'coast':
      return normalize(velocity);
  }
}

/**
 * Estimate the delta-V still required to circularise from the current state, m/s.
 *
 * Used for the live delta-V budget display and as the optimizer's objective. The
 * two terms are the burn to raise apoapsis to the target and the burn to raise
 * periapsis once there.
 */
export function remainingCircularisationDeltaV(
  position: ReadonlyVec3,
  velocity: ReadonlyVec3,
  targetAltitude: number,
  body: CelestialBodyParams = EARTH,
): number {
  const orbit = describeOrbit(position, velocity, body);
  const targetRadius = body.equatorialRadius + targetAltitude;
  const mu = body.mu;

  // Raise apoapsis to the target from the current radius.
  const currentRadius = orbit.radius;
  let total = 0;

  if (orbit.apoapsisRadius < targetRadius) {
    const aTransfer = (currentRadius + targetRadius) / 2;
    const vNow = Math.sqrt(Math.max(0, mu * (2 / currentRadius - 1 / orbit.elements.semiMajorAxis)));
    const vNeeded = Math.sqrt(mu * (2 / currentRadius - 1 / aTransfer));
    total += Math.abs(vNeeded - vNow);
  }

  // Then circularise at the target radius.
  const apoapsis = Math.max(orbit.apoapsisRadius, targetRadius);
  const aAtArrival = (Math.min(orbit.periapsisRadius, targetRadius) + apoapsis) / 2;
  const vAtApoapsis = Math.sqrt(Math.max(0, mu * (2 / apoapsis - 1 / aAtArrival)));
  const vCircular = Math.sqrt(mu / apoapsis);
  total += Math.abs(vCircular - vAtApoapsis);

  return total;
}
